import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle, Loader2, Phone } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("Si cette adresse email existe, un lien a été envoyé.");
    } catch (error) {
      toast.error("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Logos en haut */}
        <div className="flex items-center justify-center gap-6 py-6 bg-white shadow-sm">
          <img 
            src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/mvf8jz61_branding.png" 
            alt="Guinée"
            className="h-16 object-contain"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_workbilan-guinee/artifacts/0ps83xz6_WhatsApp%20Image%202025-08-28%20at%2011.04.26.jpeg" 
            alt="Ministère du Travail et de la Fonction Publique"
            className="h-20 object-contain"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/jl6j0vv2_t%C3%A9l%C3%A9chargement.jpeg" 
            alt="Programme Simandou 2040"
            className="h-16 object-contain"
          />
        </div>

        {/* Contenu principal */}
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-xl text-slate-900">Email envoyé !</CardTitle>
              <CardDescription>
                Si un compte existe avec l'adresse <strong>{email}</strong>, vous recevrez un lien de réinitialisation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600 text-center">
                Vérifiez votre boîte de réception et vos spams. Le lien est valable pendant 1 heure.
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                  className="w-full"
                >
                  Renvoyer un email
                </Button>
                <Link to="/login" className="w-full">
                  <Button variant="default" className="w-full bg-primary">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à la connexion
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 py-4 px-6">
          <div className="max-w-md mx-auto text-center space-y-3">
            <div className="flex items-center justify-center gap-6 text-sm text-slate-700">
              <a href="tel:+224613139393" className="flex items-center gap-1 hover:text-primary">
                <Phone className="w-4 h-4" />
                +224 613 13 93 93
              </a>
              <a href="mailto:info@igt.gov.gn" className="flex items-center gap-1 hover:text-primary">
                <Mail className="w-4 h-4" />
                info@igt.gov.gn
              </a>
            </div>
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} Inspection Générale du Travail - République de Guinée
            </p>
            <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
              <strong>Note:</strong> Seuls les cabinets habilités par l'IGT ont accès à cette plateforme.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Logos en haut */}
      <div className="flex items-center justify-center gap-6 py-6 bg-white shadow-sm">
        <img 
          src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/mvf8jz61_branding.png" 
          alt="Guinée"
          className="h-16 object-contain"
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_workbilan-guinee/artifacts/0ps83xz6_WhatsApp%20Image%202025-08-28%20at%2011.04.26.jpeg" 
          alt="Ministère du Travail et de la Fonction Publique"
          className="h-20 object-contain"
        />
        <img 
          src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/jl6j0vv2_t%C3%A9l%C3%A9chargement.jpeg" 
          alt="Programme Simandou 2040"
          className="h-16 object-contain"
        />
      </div>

      {/* Contenu principal */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl text-slate-900">Mot de passe oublié ?</CardTitle>
            <CardDescription>
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  "Envoyer le lien de réinitialisation"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 px-6">
        <div className="max-w-md mx-auto text-center space-y-3">
          <div className="flex items-center justify-center gap-6 text-sm text-slate-700">
            <a href="tel:+224613139393" className="flex items-center gap-1 hover:text-primary">
              <Phone className="w-4 h-4" />
              +224 613 13 93 93
            </a>
            <a href="mailto:info@igt.gov.gn" className="flex items-center gap-1 hover:text-primary">
              <Mail className="w-4 h-4" />
              info@igt.gov.gn
            </a>
          </div>
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Inspection Générale du Travail - République de Guinée
          </p>
          <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
            <strong>Note:</strong> Seuls les cabinets habilités par l'IGT ont accès à cette plateforme.
          </p>
        </div>
      </footer>
    </div>
  );
}
